// views/Profile/components/AccountTransactionsDrawer.jsx
import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import useTransactions from '../../../hooks/billing/useTransactions';
import TransactionsTable from './TransactionsTable';
import './AccountTransactionsDrawer.css';

/**
 * AccountTransactionsDrawer — right-anchored drawer that lets an org
 * admin drill into the ledger of a specific member account from the
 * UsageByAccountTable.
 *
 * Owns its own useTransactions hook keyed on `accountId`. The drawer
 * renders nothing while closed; on open it triggers the initial
 * fetch automatically via the hook's auto-fetch behavior. Closing
 * unmounts the table so a new selection always starts on page 1.
 *
 * Filters are intentionally not exposed in this drawer — admins drop
 * to the TransactionsTable for raw triage; range filtering happens
 * upstream on UsageByAccountTable. We can add filters here later if
 * needed.
 */
export default function AccountTransactionsDrawer({
  open,
  accountId,
  accountName,
  onClose,
}) {
  const closeButtonRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    previousFocusRef.current = document.activeElement;
    closeButtonRef.current?.focus();

    const onKey = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (previousFocusRef.current && previousFocusRef.current.focus) {
        previousFocusRef.current.focus();
      }
    };
  }, [open, onClose]);

  // Hooks must be called unconditionally; gate the network with the
  // auto flag so closed state issues no requests.
  const {
    items,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    refetch,
  } = useTransactions(open ? accountId : null, { auto: open });

  if (!open) return null;

  return (
    <div
      className="account-tx-drawer__backdrop"
      onClick={onClose}
      role="presentation"
    >
      <aside
        className="account-tx-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={`Transactions for ${accountName ?? accountId}`}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="account-tx-drawer__header">
          <div className="account-tx-drawer__title">
            <span className="account-tx-drawer__label">Account</span>
            <h2 className="account-tx-drawer__name">
              {accountName ?? accountId}
            </h2>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="account-tx-drawer__close"
            onClick={onClose}
            aria-label="Close drawer"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </header>

        <div className="account-tx-drawer__body">
          <TransactionsTable
            items={items}
            loading={loading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            error={error}
            onLoadMore={loadMore}
            onResetCursor={refetch}
            title="Transactions"
          />
        </div>
      </aside>
    </div>
  );
}
